// src/pages/IntBooking.js
import React, { useEffect, useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import Select from "react-select"
import ContactSelector from "components/contacts/ContactSelector"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import axios from "axios"
import apiURl from "url"

import pakistanCitiesData from "../Cities/pakistan.json"
import citiesDataGlobal from "../Cities/cities.json"

const InternationalBooking = () => {
  document.title = "International Booking"
  const navigate = useNavigate()

  const [carriers, setCarriers] = useState([])
  const [zones, setZones] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [zoneLoading, setZoneLoading] = useState(false)

  const [dbCities, setDbCities] = useState([])
  const [pakistanCities, setPakistanCities] = useState([])
  const [citiesData, setCitiesData] = useState([])

  const [rate, setRate] = useState(null)
  const [finalPrice, setFinalPrice] = useState(null)
  const [serviceFeePercentage, setServiceFeePercentage] = useState(10)

  // HS Code states
  const [hsCodeLoadings, setHsCodeLoadings] = useState({})
  const [hsCodeErrors, setHsCodeErrors] = useState({})

  // role-based
  const role = (localStorage.getItem("role") || "").toLowerCase()
  const canEditServiceFee = role === "super_admin"
  const canEditHSCode = role === "super_admin"

  // Contact data from ContactSelector
  const [senderData, setSenderData] = useState(null)
  const [receiverData, setReceiverData] = useState(null)
  const [resetContactSelector, setResetContactSelector] = useState(0)

  // Package states
  const [packageType, setPackageType] = useState("Document")
  const [pieces, setPieces] = useState(1)
  const [packageWeight, setPackageWeight] = useState("")
  const [packageLength, setPackageLength] = useState("")
  const [packageWidth, setPackageWidth] = useState("")
  const [packageHeight, setPackageHeight] = useState("")
  const [volumetricWeight, setVolumetricWeight] = useState(0)

  // Contents (items)
  const [contentItems, setContentItems] = useState([
    {
      product: "",
      hsCode: "",
      quantity: "",
      weight: "",
      declaredValue: "",
      hsFromDb: false,
    },
  ])

  const [selectedCarrier, setSelectedCarrier] = useState(null)
  const [selectedZone, setSelectedZone] = useState(null)

  // ---- Derived options ----
  const carrierOptions = useMemo(
    () =>
      carriers
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(carrier => ({ value: carrier.id, label: carrier.name })),
    [carriers]
  )

  const zoneOptions = useMemo(
    () =>
      zones.map(zone => ({
        value: zone.id,
        label: `${zone.country_name} (${zone.country_name}) - ${zone.value}`,
        countryCode: zone.country_name,
      })),
    [zones]
  )

  // Unique country list for receiver
  const receiverCountryOptions = useMemo(() => {
    const unique = []
    const seen = new Set()

    for (const city of citiesDataGlobal) {
      if (!seen.has(city.country_name)) {
        seen.add(city.country_name)
        unique.push({
          value: city.country_name,
          label: city.country_name,
        })
      }
    }

    return unique.sort((a, b) => a.label.localeCompare(b.label))
  }, [])

  // Filter receiver cities based on selected receiver country_name
  const receiverCountryName = receiverData?.country_name || null
  const receiverCities = useMemo(() => {
    if (!receiverCountryName) return []

    const jsonCities = citiesDataGlobal
      .filter(c => c.country_name === receiverCountryName)
      .map(c => ({ name: c.name, country_name: c.country_name }))

    const dbReceiverCities = dbCities
      .filter(c => c.country_name === receiverCountryName)
      .map(c => ({ name: c.city_name, country_name: c.country_name }))

    const merged = [...jsonCities, ...dbReceiverCities]

    const seen = new Set()
    return merged.filter(c => {
      const key = c.name.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [receiverCountryName, dbCities])

  const packageTypeOptions = [
    { value: "Document", label: "Document" },
    { value: "Non-Document", label: "Non-Document" },
  ]

  // ---- API Calls ----
  const fetchCarriers = async () => {
    try {
      setIsLoading(true)
      const { data } = await axios.get(`${apiURl}/carriers/list`)
      if (data.status === "success") {
        const activeCarriers = data.carriers.filter(
          carrier => Number(carrier.status) === 1
        )
        setCarriers(activeCarriers)
      } else {
        toastr.error(data.message || "Failed to fetch carriers")
      }
    } catch (err) {
      toastr.error("Error fetching carriers")
    } finally {
      setIsLoading(false)
    }
  }

  const fetchZones = async carrierId => {
    if (!carrierId) {
      setZones([])
      setSelectedZone(null)
      setZoneLoading(false)
      return
    }
    try {
      setZoneLoading(true)
      const { data } = await axios.get(`${apiURl}/carriers/${carrierId}/zones`)
      if (data.status === "success") {
        setZones(data.data || [])
      } else {
        toastr.error(data.message || "Failed to fetch zones")
        setZones([])
      }
    } catch (err) {
      toastr.error("Error fetching zones")
      setZones([])
    } finally {
      setZoneLoading(false)
    }
  }

  const fetchRate = async (carrierId, zoneVal, weight, rateType) => {
    try {
      const { data } = await axios.get(
        `${apiURl}/carriers/${carrierId}/LeadRate?zone_value=${zoneVal}&weight=${weight}&rate_type=${rateType}`
      )
      if (data.status === "success") {
        return parseFloat(data.rate) || null
      }
      return null
    } catch (err) {
      console.error("Error fetching rate")
      return null
    }
  }

  // ---- Effects ----

  // Auto-calc volumetric weight
  useEffect(() => {
    if (packageType === "Non-Document") {
      const l = parseFloat(packageLength) || 0
      const w = parseFloat(packageWidth) || 0
      const h = parseFloat(packageHeight) || 0
      if (l > 0 && w > 0 && h > 0) {
        setVolumetricWeight((l * w * h) / 5000)
      } else {
        setVolumetricWeight(0)
      }
    } else {
      setVolumetricWeight(0)
    }
  }, [packageType, packageLength, packageWidth, packageHeight])

  useEffect(() => {
    fetchCarriers()
  }, [])

  // ================================
  // LOAD DB CITIES + MERGE WITH JSON
  // ================================

  const loadCities = async () => {
    try {
      // 1) Get DB Cities
      const res = await axios.get(`${apiURl}/international/cities-options/list`)
      const db = res.data?.cities || []
      setDbCities(db)

      // ===========================
      // MERGE SENDER (Pakistan)
      // ===========================
      const dbPakistan = db
        .filter(c => c.country_name === "Pakistan")
        .map(c => ({ name: c.city_name, country_name: c.country_name }))

      const jsonPakistan = pakistanCitiesData.map(c => ({
        name: c.name,
        country_name: c.country_name,
      }))

      const mergedPakistan = [...jsonPakistan, ...dbPakistan]

      // remove duplicates (case-insensitive)
      const seenPk = new Set()
      const uniquePakistan = mergedPakistan.filter(c => {
        const key = c.name.toLowerCase()
        if (seenPk.has(key)) return false
        seenPk.add(key)
        return true
      })

      setPakistanCities(uniquePakistan)

      // ===========================
      // MERGE GLOBAL CITIES
      // ===========================
      const dbGlobal = db.map(c => ({
        name: c.city_name,
        country_name: c.country_name,
      }))

      const jsonGlobal = citiesDataGlobal.map(c => ({
        name: c.name,
        country_name: c.country_name,
      }))

      const mergedGlobal = [...jsonGlobal, ...dbGlobal]

      // remove duplicates by (country + city)
      const seenAll = new Set()
      const uniqueGlobal = mergedGlobal.filter(c => {
        const key = `${c.country_name.toLowerCase()}|${c.name.toLowerCase()}`
        if (seenAll.has(key)) return false
        seenAll.add(key)
        return true
      })

      setCitiesData(uniqueGlobal)
    } catch (err) {
      console.error("City merge error:", err)
    }
  }
  useEffect(() => {
    loadCities()
  }, [])

  useEffect(() => {
    if (selectedCarrier?.value) {
      fetchZones(selectedCarrier.value)
    } else {
      setZones([])
      setSelectedZone(null)
    }
  }, [selectedCarrier])

  // Rate calculation
  useEffect(() => {
    const calculateRate = async () => {
      if (selectedCarrier && selectedZone && packageWeight && packageType) {
        const pkgWeight = parseFloat(packageWeight)
        if (isNaN(pkgWeight) || pkgWeight <= 0) {
          setRate(null)
          setFinalPrice(null)
          return
        }

        const chargeableWeight =
          volumetricWeight && volumetricWeight > pkgWeight
            ? volumetricWeight
            : pkgWeight

        const zoneId = selectedZone.value
        const selectedZoneObj = zones.find(z => z.id === parseInt(zoneId))
        const zoneVal = selectedZoneObj ? parseInt(selectedZoneObj.value) : null
        if (!zoneVal) {
          setRate(null)
          setFinalPrice(null)
          return
        }
        const rateType = packageType === "Document" ? 1 : 2
        const baseRate = await fetchRate(
          selectedCarrier.value,
          zoneVal,
          chargeableWeight,
          rateType
        )
        if (baseRate !== null) {
          const percentage = serviceFeePercentage / 100
          const total = baseRate * (1 + percentage)
          setRate(baseRate)
          setFinalPrice(total)
        } else {
          setRate(null)
          setFinalPrice(null)
        }
      } else {
        setRate(null)
        setFinalPrice(null)
      }
    }

    calculateRate()
  }, [
    selectedCarrier,
    selectedZone,
    packageWeight,
    packageType,
    zones,
    serviceFeePercentage,
    volumetricWeight,
  ])

  // ---- Handlers ----

  const handleCarrierChange = selectedOption => {
    setSelectedCarrier(selectedOption)
    setSelectedZone(null)
  }

  const handleZoneChange = selectedOption => {
    setSelectedZone(selectedOption)
  }

  const handlePackageTypeChange = selectedOption => {
    setPackageType(selectedOption.value)
  }

  // HS Code lookup for a product in contents
  const fetchHSCodeForProduct = async (productName, index) => {
    const name = (productName || "").trim()
    if (!name) return

    setHsCodeLoadings(prev => ({ ...prev, [index]: true }))
    setHsCodeErrors(prev => ({ ...prev, [index]: null }))

    try {
      const { data } = await axios.get(`${apiURl}/hs-codes/lookup`, {
        params: { product: name },
      })
      if (data.status === "success" && data.hs_code) {
        setContentItems(prev =>
          prev.map((item, idx) =>
            idx === index
              ? { ...item, hsCode: data.hs_code, hsFromDb: true }
              : item
          )
        )
        toastr.success(`HS Code found for "${name}"`)
      } else {
        setContentItems(prev =>
          prev.map((item, idx) =>
            idx === index ? { ...item, hsFromDb: false } : item
          )
        )
        setHsCodeErrors(prev => ({
          ...prev,
          [index]: `No HS Code found for "${name}".`,
        }))
      }
    } catch (err) {
      console.error("Error fetching HS code", err)
      setHsCodeErrors(prev => ({
        ...prev,
        [index]: "Error fetching HS Code. Please enter manually.",
      }))
    } finally {
      setHsCodeLoadings(prev => ({ ...prev, [index]: false }))
    }
  }

  const saveHSCodeToDB = async (product, hsCode) => {
    try {
      await axios.post(`${apiURl}/hs-codes/add`, {
        product: product.trim(),
        hs_code: hsCode.trim(),
        created_by: localStorage.getItem("user_id") || null,
      })
      toastr.success("HS Code saved to database")
    } catch (err) {
      console.error("Error saving HS Code:", err)
      toastr.error("Could not save HS Code")
    }
  }

  const handleContentChange = (index, field, value) => {
    setContentItems(prev =>
      prev.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      )
    )

    if (field === "product" && hsCodeErrors[index]) {
      setHsCodeErrors(prev => ({ ...prev, [index]: null }))
    }
  }

  const handleProductBlur = (index, product) => {
    fetchHSCodeForProduct(product, index)
  }

  const handleAddContentRow = () => {
    setContentItems(prev => [
      ...prev,
      {
        product: "",
        hsCode: "",
        quantity: "",
        weight: "",
        declaredValue: "",
        hsFromDb: false,
      },
    ])
  }

  const handleRemoveContentRow = index => {
    setContentItems(prev => prev.filter((_, idx) => idx !== index))
    setHsCodeLoadings(prev => {
      const newLoadings = { ...prev }
      delete newLoadings[index]
      return newLoadings
    })
    setHsCodeErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[index]
      return newErrors
    })
  }

  // ---- Validation ----

  const validateForm = () => {
    // Sender validation
    if (
      !senderData ||
      !senderData.first_name?.trim() ||
      !senderData.last_name?.trim() ||
      !senderData.phone?.trim() ||
      !senderData.city_name ||
      !(senderData.current_address || "").trim()
    ) {
      toastr.error("Please complete Shipper details")
      return false
    }

    // Receiver validation
    if (
      !receiverData ||
      !receiverData.first_name?.trim() ||
      !receiverData.last_name?.trim() ||
      !receiverData.phone?.trim() ||
      !receiverData.country_name ||
      !receiverData.city_name ||
      !(receiverData.full_address || "").trim()
    ) {
      toastr.error("Please complete Consignee details")
      return false
    }

    // Carrier / Zone
    if (!selectedCarrier) {
      toastr.error("Please select a carrier")
      return false
    }
    if (!selectedZone) {
      toastr.error("Please select a zone")
      return false
    }

    // Package
    const piecesInt = parseInt(pieces)
    if (isNaN(piecesInt) || piecesInt <= 0) {
      toastr.error("Pieces must be a positive number")
      return false
    }

    const pkgWeightNum = parseFloat(packageWeight)
    if (isNaN(pkgWeightNum) || pkgWeightNum <= 0) {
      toastr.error("Package weight must be a positive number")
      return false
    }

    if (serviceFeePercentage < 0 || serviceFeePercentage > 100) {
      toastr.error("Service fee percentage must be between 0 and 100")
      return false
    }

    // Validate contents
    const hasValidContent = contentItems.some(item => {
      return (
        item.product &&
        item.product.trim() &&
        item.quantity &&
        item.weight &&
        item.declaredValue
      )
    })

    if (!hasValidContent) {
      toastr.error(
        "Please add at least one content row with product, quantity, weight and declared value"
      )
      return false
    }

    for (let item of contentItems) {
      if (!item.product || !item.product.trim()) continue
      const qty = parseInt(item.quantity)
      const w = parseFloat(item.weight)
      const d = parseFloat(item.declaredValue)
      if (isNaN(qty) || qty <= 0) {
        toastr.error("Content quantity must be a positive number")
        return false
      }
      if (isNaN(w) || w <= 0) {
        toastr.error("Content weight must be a positive number")
        return false
      }
      if (isNaN(d) || d <= 0) {
        toastr.error("Content declared value must be a positive number")
        return false
      }
    }

    return true
  }

  // helper to get sender city name for initial_location
  const getSenderCityName = () => senderData?.city_name || ""

  // ---- Submit ----

  const handleSubmit = async e => {
    e.preventDefault()
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      // Save new HS codes
      for (const item of contentItems) {
        if (item.product?.trim() && item.hsCode?.trim() && !item.hsFromDb) {
          await saveHSCodeToDB(item.product, item.hsCode)
        }
      }

      const payload = {
        sender: senderData,
        receiver: receiverData,
        package: {
          type: packageType.toLowerCase(),
          pieces: parseInt(pieces),
          weight: packageWeight,
          length: packageLength || "",
          width: packageWidth || "",
          height: packageHeight || "",
          volumetric_weight: volumetricWeight || 0,
          contents: contentItems
            .filter(item => item.product && item.product.trim())
            .map(item => ({
              product: item.product,
              hs_code: item.hsCode || "",
              quantity: parseInt(item.quantity),
              weight: item.weight,
              declared_value: item.declaredValue,
            })),
        },
        carrier_id: selectedCarrier.value,
        zone_id: selectedZone.value,
        service_fee_percentage: serviceFeePercentage,
        total_price: finalPrice,
        created_by: localStorage.getItem("user_id"),
        initial_location: getSenderCityName(),
      }

      const { data } = await axios.post(
        `${apiURl}/international/bookings/create`,
        payload
      )
      if (data.status === "success") {
        toastr.success("Booking created successfully")
        navigate("/int-bookings")
      } else {
        toastr.error(data.message || "Failed to create booking")
      }
    } catch (err) {
      toastr.error("Error creating booking")
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setSenderData(null)
    setReceiverData(null)

    setPackageType("Document")
    setPieces(1)
    setPackageWeight("")
    setPackageLength("")
    setPackageWidth("")
    setPackageHeight("")
    setVolumetricWeight(0)

    setContentItems([
      {
        product: "",
        hsCode: "",
        quantity: "",
        weight: "",
        declaredValue: "",
        hsFromDb: false,
      },
    ])

    setSelectedCarrier(null)
    setSelectedZone(null)
    setZones([])
    setRate(null)
    setFinalPrice(null)
    setServiceFeePercentage(10)

    setHsCodeLoadings({})
    setHsCodeErrors({})

    setResetContactSelector(prev => prev + 1)
  }

  // ---- Loading state ----
  if (isLoading && carriers.length === 0) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner
              color="primary"
              style={{ width: "3rem", height: "3rem" }}
            />
            <p className="mt-2">Loading application...</p>
          </div>
        </Container>
      </div>
    )
  }

  const selectTheme = theme => ({
    ...theme,
    borderRadius: 8,
    colors: {
      ...theme.colors,
      primary25: "#e3f2fd",
      primary: "#2196f3",
    },
  })

  const selectStyles = {
    control: base => ({
      ...base,
      minHeight: "22px",
      fontSize: "0.7rem",
      border: "2px solid #e0e0e0",
      borderRadius: 8,
      borderColor: "#e0e0e0",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#2196f3",
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#2196f3"
        : state.isFocused
        ? "#f0f8ff"
        : "white",
      color: state.isSelected ? "white" : "#333",
      fontSize: "0.7rem",
    }),
  }
  const selectStylesforSearch = {
    control: base => ({
      ...base,
      minHeight: "22px",
      fontSize: "0.7rem",
      border: "2px solid #e0e0e0",
      borderRadius: 8,
      borderColor: "#e0e0e0",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#2196f3",
      },
    }),
  }

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Row className="mb-2">
            <Col xs="12">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h3 className="mb-1 fw-bold text-primary">
                    <i className="bx bx-globe me-2"></i>
                    International Courier Booking
                  </h3>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    color="outline-secondary"
                    size="sm"
                    className="px-4 py-2 sleek-btn"
                    onClick={resetForm}
                  >
                    <i className="bx bx-reset me-1"></i>
                    Reset Form
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <Form onSubmit={handleSubmit}>
                <Row>
                  {/* Shipper */}
                  <Col lg="3">
                    <div className="sender-section">
                      <h6 className="fw-bold text-primary mb-3 border-bottom border-1">
                        Shipper Details
                      </h6>
                      <ContactSelector
                        type="sender"
                        enableSearch={true}
                        citiesData={pakistanCities}
                        onChange={data => setSenderData(data)}
                        selectTheme={selectTheme}
                        selectStyles={selectStyles}
                        selectStylesforSearch={selectStylesforSearch}
                        resetSignal={resetContactSelector}
                        loadCities={loadCities}
                      />
                    </div>
                  </Col>

                  {/* Consignee */}
                  <Col lg="3">
                    <div className="sender-section">
                      <h6 className="fw-bold text-primary mb-3 border-bottom border-1">
                        <i className="bx bx-receive me-2"></i>
                        Consignee Detail
                      </h6>
                      <ContactSelector
                        type="receiver"
                        enableSearch={true}
                        citiesData={receiverCities}
                        receiverCountryOptions={receiverCountryOptions}
                        onChange={data => setReceiverData(data)}
                        selectTheme={selectTheme}
                        selectStyles={selectStyles}
                        selectStylesforSearch={selectStylesforSearch}
                        resetSignal={resetContactSelector}
                        loadCities={loadCities}
                      />
                    </div>
                  </Col>

                  {/* Package Details */}
                  <Col lg="3">
                    <div className="sender-section">
                      <h6 className="fw-bold text-primary mb-3 border-bottom border-1">
                        Package Details
                      </h6>
                      <Row>
                        <Col md={12}>
                          <FormGroup>
                            <Label className="fw-semibold text-dark">
                              Package Type{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <Select
                              classNamePrefix="select2-selection"
                              options={packageTypeOptions}
                              value={packageTypeOptions.find(
                                opt => opt.value === packageType
                              )}
                              onChange={handlePackageTypeChange}
                              placeholder="Select Type"
                              theme={selectTheme}
                              styles={selectStyles}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label className="fw-semibold text-dark">
                              Pieces <span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="number"
                              className="form-control sleek-input"
                              value={pieces}
                              onChange={e => setPieces(e.target.value)}
                              placeholder="e.g., 1"
                              min="1"
                              required
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label className="fw-semibold text-dark">
                              Weight (kg) <span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              className="form-control sleek-input"
                              value={packageWeight}
                              onChange={e => setPackageWeight(e.target.value)}
                              placeholder="e.g., 0.5"
                              required
                            />
                          </FormGroup>
                        </Col>
                        <Col md={12}>
                          <FormGroup>
                            <Label className="fw-semibold text-muted">
                              Chargeable Weight
                            </Label>
                            <Input
                              type="text"
                              className="form-control sleek-input bg-light"
                              value={
                                volumetricWeight >
                                (parseFloat(packageWeight) || 0)
                                  ? `${volumetricWeight.toFixed(
                                      2
                                    )} (Volumetric)`
                                  : `${packageWeight || ""} (Actual)`
                              }
                              readOnly
                              placeholder="Auto-calculated"
                            />
                            <small className="text-muted">
                              Max of actual & volumetric
                            </small>
                          </FormGroup>
                        </Col>

                        {packageType === "Non-Document" && (
                          <>
                            <Col md={4}>
                              <FormGroup>
                                <Label className="fw-semibold text-dark">
                                  L (cm)
                                </Label>
                                <Input
                                  type="number"
                                  className="form-control sleek-input"
                                  value={packageLength}
                                  onChange={e =>
                                    setPackageLength(e.target.value)
                                  }
                                  placeholder="e.g., 20"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup>
                                <Label className="fw-semibold text-dark">
                                  W (cm)
                                </Label>
                                <Input
                                  type="number"
                                  className="form-control sleek-input"
                                  value={packageWidth}
                                  onChange={e =>
                                    setPackageWidth(e.target.value)
                                  }
                                  placeholder="e.g., 15"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup>
                                <Label className="fw-semibold text-dark">
                                  H (cm)
                                </Label>
                                <Input
                                  type="number"
                                  className="form-control sleek-input"
                                  value={packageHeight}
                                  onChange={e =>
                                    setPackageHeight(e.target.value)
                                  }
                                  placeholder="e.g., 10"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={12}>
                              <FormGroup>
                                <Label className="fw-semibold text-dark">
                                  Volumetric Weight (kg)
                                </Label>
                                <div className="input-group">
                                  <Input
                                    type="text"
                                    className="form-control sleek-input bg-light"
                                    value={
                                      volumetricWeight
                                        ? `${volumetricWeight.toFixed(2)}`
                                        : ""
                                    }
                                    readOnly
                                    placeholder="Auto-calculated"
                                  />
                                  <span className="input-group-text bg-primary text-white">
                                    (L×W×H)/5000
                                  </span>
                                </div>
                              </FormGroup>
                            </Col>
                          </>
                        )}
                      </Row>
                    </div>
                  </Col>

                  {/* Content Items */}
                  <Col lg="3">
                    <div className="sender-section">
                      <h6 className="fw-bold text-primary mb-3 border-bottom border-1">
                        Content Items
                      </h6>

                      {contentItems.map((item, index) => (
                        <div key={index}>
                          <Row className="mb-3">
                            {/* Product */}
                            <Col md={12}>
                              <FormGroup>
                                <Label className="fw-semibold text-dark">
                                  Product <span className="text-danger">*</span>
                                </Label>
                                <div className="position-relative">
                                  <Input
                                    type="text"
                                    className="form-control sleek-input"
                                    value={item.product}
                                    onChange={e =>
                                      handleContentChange(
                                        index,
                                        "product",
                                        e.target.value
                                      )
                                    }
                                    onBlur={e =>
                                      handleProductBlur(index, e.target.value)
                                    }
                                    placeholder={
                                      hsCodeLoadings[index]
                                        ? ""
                                        : "Type product name"
                                    }
                                  />
                                  {hsCodeLoadings[index] && (
                                    <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                                      <Spinner size="sm" />
                                    </div>
                                  )}
                                </div>
                              </FormGroup>
                            </Col>

                            {/* HS Code */}
                            <Col md={12}>
                              <FormGroup>
                                <Label className="fw-semibold text-dark">
                                  HS Code
                                </Label>
                                <Input
                                  type="text"
                                  className="form-control sleek-input"
                                  value={item.hsCode}
                                  onChange={e =>
                                    handleContentChange(
                                      index,
                                      "hsCode",
                                      e.target.value
                                    )
                                  }
                                  placeholder={
                                    hsCodeLoadings[index]
                                      ? "Loading HS Code..."
                                      : "Will auto-fill"
                                  }
                                  readOnly={item.hsFromDb && !canEditHSCode}
                                />
                                {hsCodeErrors[index] && (
                                  <small className="text-danger">
                                    {hsCodeErrors[index]}
                                  </small>
                                )}
                                {item.hsFromDb && (
                                  <small className="text-success d-block mt-1">
                                    <i className="bx bx-check-circle me-1"></i>{" "}
                                    From database
                                  </small>
                                )}
                              </FormGroup>
                            </Col>

                            {/* Quantity */}
                            <Col md={6}>
                              <FormGroup>
                                <Label className="fw-semibold text-dark">
                                  Quantity{" "}
                                  <span className="text-danger">*</span>
                                </Label>
                                <Input
                                  type="number"
                                  className="form-control sleek-input"
                                  value={item.quantity}
                                  placeholder="e.g., 01"
                                  onChange={e =>
                                    handleContentChange(
                                      index,
                                      "quantity",
                                      e.target.value
                                    )
                                  }
                                  min="1"
                                />
                              </FormGroup>
                            </Col>

                            {/* Weight */}
                            <Col md={6}>
                              <FormGroup>
                                <Label className="fw-semibold text-dark">
                                  Weight (kg){" "}
                                  <span className="text-danger">*</span>
                                </Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="e.g., 01"
                                  className="form-control sleek-input"
                                  value={item.weight}
                                  onChange={e =>
                                    handleContentChange(
                                      index,
                                      "weight",
                                      e.target.value
                                    )
                                  }
                                />
                              </FormGroup>
                            </Col>

                            {/* Declared Value */}
                            <Col md={12}>
                              <FormGroup>
                                <Label className="fw-semibold text-dark">
                                  Declared Value (PKR){" "}
                                  <span className="text-danger">*</span>
                                </Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="e.g., 10000"
                                  className="form-control sleek-input"
                                  value={item.declaredValue}
                                  onChange={e =>
                                    handleContentChange(
                                      index,
                                      "declaredValue",
                                      e.target.value
                                    )
                                  }
                                />
                              </FormGroup>
                            </Col>

                            {/* Action Buttons */}
                            <Col
                              md={12}
                              className="d-flex justify-content-between"
                            >
                              <Button
                                color="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveContentRow(index)}
                                disabled={contentItems.length === 1}
                              >
                                <i className="bx bx-trash"></i> Remove
                              </Button>

                              {index === contentItems.length - 1 && (
                                <Button
                                  color="outline-primary"
                                  size="sm"
                                  onClick={handleAddContentRow}
                                >
                                  <i className="bx bx-plus"></i> Add More
                                </Button>
                              )}
                            </Col>
                          </Row>
                        </div>
                      ))}
                    </div>
                  </Col>

                  {/* Pricing & Submit */}
                  <Col lg="12">
                    <Card className="mt-4 shadow-sm sleek-pricing-card">
                      <CardBody>
                        <Row>
                          <Col md={4}>
                            <FormGroup>
                              <Label className="fw-semibold text-dark">
                                Carrier <span className="text-danger">*</span>
                              </Label>
                              <Select
                                classNamePrefix="select2-selection"
                                options={carrierOptions}
                                value={selectedCarrier}
                                onChange={handleCarrierChange}
                                placeholder="Select Carrier"
                                isSearchable
                                theme={selectTheme}
                                styles={selectStyles}
                                menuPlacement="top"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label className="fw-semibold text-dark">
                                Zone/Country{" "}
                                <span className="text-danger">*</span>
                              </Label>
                              <Select
                                classNamePrefix="select2-selection"
                                options={zoneOptions}
                                value={selectedZone}
                                onChange={handleZoneChange}
                                placeholder={
                                  zoneLoading
                                    ? "Loading zones..."
                                    : "Select Zone"
                                }
                                isSearchable
                                isDisabled={!selectedCarrier || zoneLoading}
                                isLoading={zoneLoading}
                                theme={selectTheme}
                                styles={selectStyles}
                                menuPlacement="top"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label className="fw-semibold text-dark">
                                Service Fee (%){" "}
                                <span className="text-danger">*</span>
                              </Label>
                              <Input
                                type="number"
                                step="0.1"
                                min="0"
                                max="100"
                                className="form-control sleek-input"
                                value={serviceFeePercentage}
                                onChange={e =>
                                  setServiceFeePercentage(
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                placeholder="e.g., 10"
                                required
                                disabled={!canEditServiceFee}
                              />
                              {!canEditServiceFee && (
                                <small className="text-muted">
                                  <i className="bx bx-lock-alt me-1"></i>
                                  Super admin only
                                </small>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>

                        {rate && finalPrice && (
                          <>
                            <h6 className="mb-2 fw-bold text-primary">
                              <i className="bx bx-calculator me-2"></i>
                              Pricing Summary
                            </h6>
                            <Row className="g-3">
                              <Col md={4}>
                                <div className="p-2 rounded-3 bg-light border sleek-price-box">
                                  <div className="text-muted small fw-medium">
                                    Base Rate
                                  </div>
                                  <div className="fw-bold fs-5 text-primary mt-1">
                                    ${rate.toFixed(2)}
                                  </div>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="p-2 rounded-3 bg-light border sleek-price-box">
                                  <div className="text-muted small fw-medium">
                                    Service Fee ({serviceFeePercentage}%)
                                  </div>
                                  <div className="fw-bold fs-5 text-warning mt-1">
                                    $
                                    {(
                                      rate *
                                      (serviceFeePercentage / 100)
                                    ).toFixed(2)}
                                  </div>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="p-2 rounded-3 bg-gradient bg-success text-white sleek-price-box">
                                  <div className="text-white-50 small fw-medium">
                                    Total Price (Base + Fee)
                                  </div>
                                  <div className="fw-bold fs-4 mt-1">
                                    PKR {finalPrice.toFixed(2)}
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </>
                        )}

                        <div className="mt-3 float-end d-flex gap-3">
                          <Button
                            size="lg"
                            type="button"
                            onClick={resetForm}
                            className="btn btn-sm"
                          >
                            Cancel
                          </Button>
                          <Button
                            color="primary"
                            size="lg"
                            type="submit"
                            disabled={isSubmitting || zoneLoading}
                            className="btn btn-sm"
                          >
                            {isSubmitting ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Creating Booking...
                              </>
                            ) : (
                              <>
                                <i className="bx bx-plus"></i> Create Booking
                              </>
                            )}
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .sleek-card {
          border-radius: 16px;
          background: #fff;
        }
        .sleek-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 12px 12px 0 0 !important;
          border: none;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        .sleek-header:hover {
          background: linear-gradient(135deg, #e3f2fd 0%, #f0f8ff 100%);
        }
        .sleek-input {
          border-radius: 8px;
          border: 2px solid #e0e0e0;
          padding: 8px 12px;
          font-size: 0.7rem;
          transition: all 0.2s ease;
          background: #fff;
        }
        .sleek-input:focus {
          border-color: #2196f3;
          box-shadow: 0 0 0 0.2rem rgba(33, 150, 243, 0.15);
        }
        .sleek-input.loading {
          background-color: #f8f9fa;
          cursor: progress;
        }
        .sleek-btn {
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .sleek-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        .sender-section, .receiver-section {
          padding: 15px;
          background: #fff;
          border-radius: 10px;
          border-top: 3px solid #FE9C02;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        .sleek-pricing-card {
          border-top: 3px solid #FE9C02;
          border-radius: 12px;
        }
        .sleek-price-box {
          border-radius: 8px;
          text-align: center;
          transition: transform 0.2s ease;
        }
        .sleek-price-box:hover {
          transform: translateY(-2px);
        }
        .bg-gradient {
          background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
        }
      `}</style>
    </>
  )
}

export default InternationalBooking
